/**
 * unifiedAgreements.ts — one signed record per arc, whichever generation produced it.
 *
 * THE DEFECT THIS EXISTS FOR. A contract's Documents tab grew a second generation without
 * losing the first, so an agreement signed IN THE APP printed twice: once as an in-app
 * document and once, further down, as a legacy card — the SAME PDF, because
 * `agreement_finalize` writes `signed_at`, `agreement_url` and `origin = 'IN_APP'` back onto
 * the legacy row the document was generated for.
 *
 * The two are not two records. They are one record seen through two reads, and the thing that
 * says so is THE ARC: the exclusive foreign key on `agreement_documents` naming the legacy row
 * a document was raised against. So the list is built by arc, and a legacy row whose arc holds
 * an in-app document is not rendered at all — the in-app card is the canonical one.
 *
 * ── WHY THE KEY IS DERIVED HERE RATHER THAN READ OFF THE ARC COLUMNS ──────────
 * The board reads `agreement_documents` a second time for its arc ids
 * (`fetchAgreementDocumentArcs`) and matches BY ARC ID, which is the stronger join. This
 * surface cannot: `get_contract_insights` projects the legacy contract agreements as
 * `{ type, signed_at, agreement_url }` with NO row id, so there is nothing on that side for a
 * `contract_agreement_id` to match. Both arcs it shows are identified by what
 * `get_agreement_documents` already returns:
 *
 *   contract arc   `uq_contract_agreements_contract_type` makes (contract, type) unique, and
 *                  `agreement_create` maps HIRE_PURCHASE -> DRIVER and WITNESS -> WITNESS. One
 *                  contract has at most one legacy row per doc type, so the doc type IS the arc.
 *   deposit arc    `path_scope` is `deposit/<contract_deposit_id>`, derived server-side by
 *                  `agreement_path_scope` — arc first, contract never consulted — and the
 *                  insights row carries `contract_deposit_id`. An exact join, per deposit.
 *
 * THE DOC-TYPE SHORTCUT IS REFUSED FOR EVERY OTHER TYPE, which is the reasoning `boardSlots.ts`
 * states for itself: one contract holds several deposits and several loans, so a key made of the
 * type alone would let one document suppress every legacy row of its kind. Loan and
 * insurance-loan documents therefore get no arc key at all — this tab has never listed their
 * legacy rows (`loan_agreements` and `insurance_loan_agreements` are in the insights payload and
 * have no consumer), so there is nothing of theirs to deduplicate against.
 *
 * THE VEHICLE SCHEDULE JOINS THEM, AND FOR A STRONGER REASON [D-B48]: `get_contract_insights`
 * projects no `contract_partner_agreements` AT ALL, so there is no legacy side on this payload for
 * a schedule to be a twin of. A key of the doc type would be sound here — one contract has one
 * vehicle and therefore one live schedule — and it would still have nothing to suppress. THE
 * SCOPE BOUNDARY THAT FOLLOWS IS THE POINT: an in-app schedule shows on the Documents tab as
 * itself, and a schedule that exists only as a pre-app scan does not appear there, because a scan
 * reaches this list only through a legacy row the payload does not carry. Its home is the setup
 * canvas and the partner's own record.
 *
 * NOT IN `documents.ts`, WHICH RESTATES SQL. That module holds the two derivations
 * `get_agreement_documents` performs in the database, for surfaces that cannot call it. This is
 * not one of those: it is a presentation rule about two generations of one record, and the
 * server has no opinion on it. NOT IN `boardSlots.ts` either — a slot exists so an admin can
 * ACT on a subject that may have no document at all, and is built from four subject queries plus
 * the arc read. This surface only ever presents what was signed.
 */

/** `agreement_create`'s own HIRE_PURCHASE/WITNESS -> DRIVER/WITNESS mapping. */
const CONTRACT_ARC_TYPE_BY_DOC_TYPE: Record<string, string> = {
    HIRE_PURCHASE: 'DRIVER',
    WITNESS: 'WITNESS',
};

/** The same mapping read backwards, so a scan is titled what its in-app twin would be. */
const DOC_TYPE_BY_CONTRACT_ARC_TYPE: Record<string, string> = {
    DRIVER: 'HIRE_PURCHASE',
    WITNESS: 'WITNESS',
};

/** The deposit half of `agreement_path_scope`. The other half is a bare contract id. */
const DEPOSIT_SCOPE = /^deposit\/([0-9]+)$/;

/**
 * Document statuses in which a signature can still be taken.
 *
 * DELIBERATELY NOT `isLiveDocument` FROM `constants.ts`. That predicate answers a different
 * question — which statuses hold an arc's slot — and it counts COMPLETED, because a completed
 * document still occupies its arc. Splitting on it would file every finished agreement under a
 * heading that says it is still in flight.
 */
const OPEN_DOCUMENT_STATUSES = ['PENDING_REVIEW', 'IN_SIGNING'];

function isOpenDocument(document: { status: string }): boolean {
    return OPEN_DOCUMENT_STATUSES.includes(document.status);
}

/** The columns an arc key is derived from. */
export interface ArcedDocument {
    doc_type: string;
    path_scope: string | null;
}

/** The columns the split and the ordering read. */
export interface GroupableDocument {
    status: string;
    created_at: string;
    completed_at: string | null;
}

/** What the unified list needs off a `get_agreement_documents` row. */
export interface UnifiableDocument extends GroupableDocument, ArcedDocument {
    id: number;
}

export function contractArcKey(legacyType: string): string {
    return `contract:${legacyType}`;
}

export function depositArcKey(contractDepositId: number | string): string {
    return `deposit:${contractDepositId}`;
}

/** The arc an in-app document was raised against, or null where this tab shows no twin. */
export function documentArcKey(document: ArcedDocument): string | null {
    const legacyType = CONTRACT_ARC_TYPE_BY_DOC_TYPE[document.doc_type];
    if (legacyType) return contractArcKey(legacyType);

    if (document.doc_type === 'DEPOSIT') {
        const depositId = DEPOSIT_SCOPE.exec(document.path_scope ?? '')?.[1];
        return depositId ? depositArcKey(depositId) : null;
    }

    return null;
}

/**
 * Whether a document OCCUPIES its arc — a denylist of the two statuses that release it.
 *
 * The same rule the partial unique index behind every arc is keyed on
 * (`status NOT IN ('VOIDED','SUPERSEDED')`), and a denylist for the reason that index is one: a
 * withdrawn or replaced document binds nobody, so the legacy row underneath it is a free slot that
 * the next generation of that instrument occupies. Written as an allowlist, a status added
 * upstream would quietly stop suppressing its own duplicate and this tab would print the record
 * twice again.
 *
 * "FREE TO BE SIGNED ON PAPER AGAIN" IS WHAT THAT SENTENCE USED TO SAY [D-B49], and it named a
 * route no client has any more: `authenticated` has lost INSERT and UPDATE on `signed_at`,
 * `agreement_url` and `origin` across all five legacy arcs, and the board's "Record paper signing"
 * item is gone with them. What the predicate DOES is unchanged — it is the arc-occupancy denylist,
 * and a released arc is still released — so only the description of what a released arc is for
 * moved.
 */
export function holdsArc(document: { status: string }): boolean {
    return document.status !== 'VOIDED' && document.status !== 'SUPERSEDED';
}

/** One row of the insights payload's `agreements[]` — a scanned contract agreement. */
export interface LegacyContractAgreement {
    /** `contract_agreements.type`: DRIVER or WITNESS. */
    type: string;
    signed_at: string | null;
    agreement_url: string | null;
}

/** One row of the insights payload's `deposit_agreements[]`. */
export interface LegacyDepositAgreement {
    id: number;
    contract_deposit_id: number;
    signed_at: string | null;
    agreement_url: string | null;
    deposit_amount: number;
    deposit_date: string;
}

/** A signed record that exists only as a scan — no in-app document was ever raised for it. */
export interface PaperAgreement {
    /** React key, and the identity a test asserts on. */
    key: string;
    arcKey: string;
    /** Whose title this card takes, so both generations of a record read the same. */
    docType: string;
    /** Never null — an unsigned legacy row is not a signed agreement, and has no card. */
    signedAt: string;
    /** The scan. Null where the row carries a date but no file was attached. */
    url: string | null;
    /** Deposit cards keep the money and the paid date they have always shown. */
    depositAmount: number | null;
    depositDate: string | null;
}

/** One card in the unified list, from whichever generation it came. */
export type UnifiedAgreement<T> =
    | { kind: 'document'; key: string; document: T }
    | { kind: 'paper'; key: string; paper: PaperAgreement };

export interface UnifiedAgreements<T> {
    /** In-app documents still in flight — being prepared, or out for signature. */
    live: T[];
    /** Everything settled, both generations, deduped by arc; withdrawn and replaced last. */
    signed: UnifiedAgreement<T>[];
}

export interface UnifiedAgreementsInput<T> {
    documents: readonly T[] | null | undefined;
    contractAgreements: readonly LegacyContractAgreement[] | null | undefined;
    depositAgreements: readonly LegacyDepositAgreement[] | null | undefined;
}

/** An unparseable or absent timestamp sorts last rather than throwing the whole order out. */
function parseTime(value: string | null | undefined): number {
    const parsed = Date.parse(value ?? '');
    return Number.isNaN(parsed) ? 0 : parsed;
}

/** Newest first, by whichever timestamp the document actually has. */
function documentTime(document: GroupableDocument): number {
    return parseTime(document.completed_at ?? document.created_at);
}

/**
 * The three buckets the unified list is built from.
 *
 * THE ASYMMETRY IS THE POINT. `live` is an ALLOWLIST — only the two statuses in which a
 * signature can still be taken may be reported as in flight, so a status this app has not met
 * yet can never fake work in progress. `closed` is then EVERYTHING ELSE, not a second allowlist
 * of VOIDED and SUPERSEDED: a document must never disappear from the record because a status was
 * added upstream.
 */
function splitAgreementDocuments<T extends GroupableDocument>(
    documents: readonly T[] | null | undefined,
): { live: T[]; completed: T[]; closed: T[] } {
    const live: T[] = [];
    const completed: T[] = [];
    const closed: T[] = [];

    for (const document of documents ?? []) {
        if (isOpenDocument(document)) live.push(document);
        else if (document.status === 'COMPLETED') completed.push(document);
        else closed.push(document);
    }

    const newestFirst = (a: T, b: T) => documentTime(b) - documentTime(a);
    completed.sort(newestFirst);
    closed.sort(newestFirst);

    // `live` keeps the server's order — `get_agreement_documents` already returns
    // `created_at DESC, id DESC`, so a re-sort here would only be a second opinion to keep in step.
    return { live, completed, closed };
}

/**
 * The whole Documents tab's agreement half, in one pure function.
 *
 * `live` is the in-app documents still in flight, untouched: a scan is nothing anybody can be
 * asked to sign. `signed` merges what is settled — completed in-app documents and
 * undeduplicated scans interleaved newest first — and then the withdrawn and replaced
 * documents, which keep their badges rather than disappearing.
 */
export function unifyAgreementDocuments<T extends UnifiableDocument>(
    input: UnifiedAgreementsInput<T>,
): UnifiedAgreements<T> {
    const { live, completed, closed } = splitAgreementDocuments(input.documents);

    // Every arc an in-app document currently occupies. Built from the WHOLE list, not from one
    // bucket: a live document suppresses its legacy twin just as a completed one does — the
    // record is being signed in the app, and the row underneath it is that same record's
    // placeholder, not a second agreement.
    const occupied = new Set<string>();
    for (const document of input.documents ?? []) {
        if (!holdsArc(document)) continue;
        const key = documentArcKey(document);
        if (key) occupied.add(key);
    }

    const paper: PaperAgreement[] = [];

    for (const row of input.contractAgreements ?? []) {
        if (!row.signed_at) continue;
        const arcKey = contractArcKey(row.type);
        if (occupied.has(arcKey)) continue;
        const docType = DOC_TYPE_BY_CONTRACT_ARC_TYPE[row.type];
        if (!docType) continue;
        paper.push({
            key: `paper-${arcKey}`,
            arcKey,
            docType,
            signedAt: row.signed_at,
            url: row.agreement_url,
            depositAmount: null,
            depositDate: null,
        });
    }

    for (const row of input.depositAgreements ?? []) {
        if (!row.signed_at) continue;
        const arcKey = depositArcKey(row.contract_deposit_id);
        if (occupied.has(arcKey)) continue;
        paper.push({
            key: `paper-${arcKey}`,
            arcKey,
            docType: 'DEPOSIT',
            signedAt: row.signed_at,
            url: row.agreement_url,
            depositAmount: row.deposit_amount,
            depositDate: row.deposit_date,
        });
    }

    const documentEntry = (document: T): UnifiedAgreement<T> => ({
        kind: 'document',
        key: `doc-${document.id}`,
        document,
    });

    // The sort key rides BESIDE the entry rather than on it: a scan is dated by its signature
    // and a document by its completion, and the two are only comparable here.
    const settled = [
        ...completed.map((document) => ({
            at: documentTime(document),
            entry: documentEntry(document),
        })),
        ...paper.map((item) => ({
            at: parseTime(item.signedAt),
            entry: { kind: 'paper', key: item.key, paper: item } as UnifiedAgreement<T>,
        })),
    ];
    settled.sort((a, b) => b.at - a.at);

    return {
        live,
        signed: [...settled.map((row) => row.entry), ...closed.map(documentEntry)],
    };
}
